import { ReferralAdminReferralsFilterEnum } from '../services/interfaces/referral-admin-referrals-filter.enum';
import {Component, OnDestroy} from '@angular/core';
import {UserService} from "../../../../@shared/services/user.service";
import {PageEvent} from "@angular/material/paginator";
import {catchError, filter, switchMap, takeUntil} from "rxjs/operators";
import {MatTableDataSource} from "@angular/material/table";
import {of, Subject} from "rxjs";
import {
  ICommonStatReferral,
  IIncomeStat,
  IReferralStatFilter,
  IReferralListFilter,
  IRefCommonStat,
  IReferralsStat
} from "../services/interfaces/referral-campaigns.interface";
import {environment} from "../../../../../environments/environment";
import {ResetReferralIncomeComponent} from "./modals/reset-referral-income/reset-referral-income.component";
import {MatDialog} from "@angular/material/dialog";
import {MatSelectChange} from "@angular/material/select";
import {
  getReferralAdminReferralsSortBy,
  REFERRAL_ADMIN_REFERRALS_SORT_OPTIONS,
  ReferralAdminReferralsSortDirectionEnum,
  ReferralAdminReferralsSortFieldEnum,
} from '../services/interfaces/referral-admin-referrals-sort.enum';

@Component({
  selector: 'app-referral-campaigns',
  templateUrl: './referral-campaigns.component.html',
  styleUrls: ['./referral-campaigns.component.scss']
})
export class ReferralCampaignsComponent implements OnDestroy {

  public isGoFast: boolean = environment.projectName === 'CSGOFAST';

  public commonStatColumns = ['count', 'code', 'activations', 'registration', 'deposits', 'depositsCount', 'payouts', 'delta'];
  public commonStatDataSource: MatTableDataSource<IRefCommonStat> = new MatTableDataSource();
  public totalStat: IRefCommonStat = {
    code: 'Всего:',
    total: 0,
    activated: 0,
    notActivated: 0,
    lost: 0,
    referralCampaignId: -1,
    payoutsCount: 0,
    registered: 0,
    depositCount: 0,
    payouts: 0,
    delta: 0,
    deposit: 0,
  };
  public referralsColumns = [
    'connected', 'name', 'date_added', 'created_at', 'code',
    'ip', 'bonus_points', 'deposit_total', 'deposit_count', 'payouts_total',
    'payouts_count', 'total_reward_coins', 'actions'];
  public referralsDataSource: MatTableDataSource<ICommonStatReferral> = new MatTableDataSource();

  public isLoading = true;
  public selectedDate!: IReferralStatFilter;
  public referredInPeriodOnly = false;
  public referralIncomeStat: IIncomeStat;
  public referralFilter = [
    {data: ReferralAdminReferralsFilterEnum.ALL, viewValue: 'Показать всех'},
    {data: ReferralAdminReferralsFilterEnum.ACTIVATED, viewValue: 'Активированные'},
    {data: ReferralAdminReferralsFilterEnum.INACTIVE, viewValue: 'НЕактивированные'},
    {data: ReferralAdminReferralsFilterEnum.LOCKED, viewValue: 'Заблокированные'},
  ];
  public selectedFilter: ReferralAdminReferralsFilterEnum = ReferralAdminReferralsFilterEnum.ALL;
  public readonly referralSortOptions = REFERRAL_ADMIN_REFERRALS_SORT_OPTIONS;
  public readonly referralSortDirections = [
    {data: ReferralAdminReferralsSortDirectionEnum.Desc, viewValue: 'По убыванию'},
    {data: ReferralAdminReferralsSortDirectionEnum.Asc, viewValue: 'По возрастанию'},
  ];
  public selectedSortField = ReferralAdminReferralsSortFieldEnum.CreatedAt;
  public selectedSortDirection = ReferralAdminReferralsSortDirectionEnum.Desc;
  public pageIndex = 0;
  public pageSize = 10;
  public referralsTotal = 0;
  public listLoading = false;
  public listError = '';
  public summaryError = '';
  private readonly summaryRequest$ = new Subject<IReferralStatFilter>();
  private readonly listRequest$ = new Subject<IReferralListFilter>();

  constructor(private userService: UserService,
              private dialog: MatDialog) {
    this.subscribeEmitters();
  };

  private readonly destroyed$: Subject<void> = new Subject<void>();

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  applyFilter(event: MatSelectChange): void {
    this.selectedFilter = event.value;
    this.pageIndex = 0;
    this.requestPage();
  }

  applySortField(event: MatSelectChange): void {
    this.selectedSortField = event.value;
    this.pageIndex = 0;
    this.requestPage();
  }

  applySortDirection(event: MatSelectChange): void {
    this.selectedSortDirection = event.value;
    this.pageIndex = 0;
    this.requestPage();
  }

  public changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.requestPage();
  }

  private requestPage(): void {
    if (!this.selectedDate) return;
    this.listLoading = true;
    this.listError = '';
    this.referralsDataSource.data = [];
    this.listRequest$.next({
      ...this.selectedDate, filter: this.selectedFilter,
      limit: this.pageSize, offset: this.pageIndex * this.pageSize,
      sortBy: getReferralAdminReferralsSortBy(
        this.selectedSortField,
        this.selectedSortDirection,
      ),
    });
  }

  public refresh(): void {
    if (!this.selectedDate) return;
    this.isLoading = true;
    this.summaryError = '';
    this.summaryRequest$.next(this.selectedDate);
    this.requestPage();
  }

  public lockAllUserRefs(): void {
    this.userService.lockAllUserRefs().subscribe(() =>
      this.refresh());
  }

  /**
   * Принудительный вывод реф-дохода (FAST_NEW_BACK-2267): reward_coins + reward_coins_moderated → reward_coins_taken
   * и зачисление на баланс. Блок «Доход» перерисовываем из ответа.
   */
  public forceWithdrawReferralIncome(): void {
    if (!confirm('Принудительно забрать весь реферальный доход (текущий + на модерации) на баланс пользователя?')) {
      return;
    }
    this.userService.forceWithdrawReferralIncome().subscribe((incomeStat: IIncomeStat) => {
      this.referralIncomeStat = incomeStat;
    });
  }

  /** Обнуление долга рефовода (FAST_NEW_BACK-2269): минусовые reward_coins кампаний → 0, затем обновляем статистику. */
  public clearReferralCampaignDebt(): void {
    if (!confirm('Обнулить долг (отрицательные reward_coins) по всем кампаниям этого рефовода?')) {
      return;
    }
    this.userService.clearReferralCampaignDebt().subscribe(() =>
      this.refresh());
  }

  public changeDateFilter(filter: IReferralStatFilter): void {
    this.pageIndex = 0;
    this.selectedDate = {
      ...filter,
      referredInPeriodOnly: this.referredInPeriodOnly,
    };
    this.refresh();
  }

  public changeReferredInPeriodOnly(checked: boolean): void {
    this.referredInPeriodOnly = checked;
    this.pageIndex = 0;
    if (!this.selectedDate) return;
    this.selectedDate = {
      ...this.selectedDate,
      referredInPeriodOnly: checked,
    };
    this.isLoading = true;
    this.refresh();
  }

  public calculateTotalStat(stat: IRefCommonStat[]): IRefCommonStat {
    return stat.reduce((acc: IRefCommonStat, item: IRefCommonStat) => {
      acc.total += Number(item.total);
      acc.activated += Number(item.activated);
      acc.notActivated += Number(item.notActivated);
      acc.lost += Number(item.lost);
      acc.registered += Number(item.registered);
      acc.depositCount += Number(item.depositCount);
      acc.deposit += Number(item.deposit);
      acc.payouts += Number(item.payouts);
      acc.payoutsCount += Number(item.payoutsCount);
      acc.delta += Number(item.delta);
      return acc;
    }, {
      code: 'Всего:',
      total: 0,
      activated: 0,
      notActivated: 0,
      lost: 0,
      referralCampaignId: -1,
      payoutsCount: 0,
      registered: 0,
      depositCount: 0,
      payouts: 0,
      delta: 0,
      deposit: 0,
    });
  }

  public resetReferralIncome(element: ICommonStatReferral | null): void {
    const params = {
      width: 'fit-content',
      height: 'fit-content',
      data: {element, date: this.selectedDate}
    }
    this.dialog.open(ResetReferralIncomeComponent, params)
      .afterClosed()
      .pipe(
        filter(v => !!v)
      )
      .subscribe(() => this.refresh());
  }

  private subscribeEmitters(): void {
    this.summaryRequest$.pipe(
      switchMap(params => this.userService.getReferralSummary(params).pipe(
        catchError(() => {
          this.summaryError = 'Не удалось загрузить статистику';
          return of(null);
        }),
      )),
      takeUntil(this.destroyed$),
    ).subscribe((stat: IReferralsStat | null) => {
      this.isLoading = false;
      if (!stat) {
        this.commonStatDataSource.data = [];
        this.totalStat = this.calculateTotalStat([]);
        return;
      }
      this.referralIncomeStat = stat.incomeStat;
      this.totalStat = this.calculateTotalStat(stat.stat);
      this.commonStatDataSource.data = stat.stat;
    });
    this.listRequest$.pipe(
      switchMap(params => this.userService.getReferralAdminReferrals(params).pipe(
        catchError(() => {
          this.listError = 'Не удалось загрузить рефералов';
          return of(null);
        }),
      )),
      takeUntil(this.destroyed$),
    ).subscribe(page => {
      this.listLoading = false;
      if (!page) {
        this.referralsDataSource.data = [];
        this.referralsTotal = 0;
        return;
      }
      this.referralsTotal = page.metadata.total;
      // После удаления/смены привязки последняя страница могла исчезнуть.
      if (this.pageIndex > 0 && this.pageIndex * this.pageSize >= this.referralsTotal) {
        this.pageIndex = Math.max(0, Math.ceil(this.referralsTotal / this.pageSize) - 1);
        this.requestPage();
        return;
      }
      this.referralsDataSource.data = page.referrals;
    });
  }
}
